import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
  numberFormat,
} from "../../utils/helpers";
import { URL_SERVICE_RATES } from "../../utils/urls";
import useGet from "../common/useGet";
import {
  API_CAR_TYPES,
  API_CONFIGURABLES,
  API_DISTRICTS,
  API_SERVICE_RATES,
  API_SERVICE_TYPES,
  API_VEHICLE_TYPES,
} from "../../utils/apis";
import { IFormSection } from "../../types/common";

export const useServiceRates = () => {
  const tableColumns = () => [
    createColumn("service_type_name", true, "Service Type"),
    createColumn("car_size_name", true, "Car Size"),
    createColumn("car_type_name", true, "Car Type"),
    createColumn("vehicle_type_name", true, "Vehicle Type"),
    createColumn("car_size_dependant", false, "Car Size Dependant"),
    createColumn("car_type_dependant", false, "Car Type Dependant"),
    createColumn("vehicle_type_dependant", false, "Vehicle Type Dependant"),
    createColumn("min_range", false, "Min Range"),
    createColumn("max_range", false, "Max Range"),
    createColumn("duration", true, "Duration"),
    createColumn("rate_type", true, "Rate Type"),
    createColumn("city_name", true, "City"),
    createColumn("amount", true, "Amount"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        amount: numberFormat(item?.amount ? item.amount : 0),
        car_size_dependant: item?.car_size_dependant ? "Yes" : "No",
        car_type_dependant: item?.car_type_dependant ? "Yes" : "No",
        vehicle_type_dependant: item?.vehicle_type_dependant ? "Yes" : "No",
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_SERVICE_RATES, item?.id),
              createEditAction(URL_SERVICE_RATES, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Service Rate Details",
          fields: {
            service_type: item?.service_type_name,
            car_size: item?.car_size_name,
            car_type: item?.car_type_name,
            vehicle_type: item?.vehicle_type_name,
            car_size_dependant: item?.car_size_dependant ? "Yes" : "No",
            car_type_dependant: item?.car_type_dependant ? "Yes" : "No",
            vehicle_type_dependant: item?.vehicle_type_dependant ? "Yes" : "No",
            min_range: item?.min_range,
            max_range: item?.max_range,
            duration: item?.duration,
            rate_type: item?.rate_type,
            city: item?.city_name,
            amount: item?.amount,
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_SERVICE_RATES);
  //   const activityFilter = "service_rates";

  return { tableColumns, tableData, viewData, formRoute };
};

export const useServiceRateForm = ({ updateFormValues }) => {
  const {
    loadData: loadServiceRates,
    loading: loadingServiceRates,
    data: serviceRates,
  } = useGet();
  const {
    loadData: loadDistricts,
    loading: loadingDistricts,
    data: districts,
  } = useGet();

  const {
    loadData: loadServiceTypes,
    loading: loadingServiceTypes,
    data: serviceTypes,
  } = useGet();

  const {
    loadData: loadVehicleTypes,
    loading: loadingVehicleTypes,
    data: vehicleTypes,
  } = useGet();

  const {
    loadData: loadCarTypes,
    loading: loadingCarTypes,
    data: carTypes,
  } = useGet();

  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizes,
  } = useGet();

  useEffect(() => {
    loadServiceRates({ api: API_SERVICE_RATES });
    loadDistricts({ api: API_DISTRICTS });
    loadServiceTypes({ api: API_SERVICE_TYPES });
    loadVehicleTypes({ api: API_VEHICLE_TYPES });
    loadCarTypes({ api: API_CAR_TYPES });
    loadCarSizes({ api: API_CONFIGURABLES, params: { key: "car_size" } });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues();
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Service Rate Details",
      fields: [
        {
          field_name: "service_type",
          label: "Service Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Service Type",
            options: serviceTypes?.results?.map((item) => ({
              value: item.id,
              label: item.name,
            })),
          },
        },
        {
          field_name: "min_range",
          label: "Min Duration Range",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter Min Duration Range",
            min: 0,
          },
        },
        {
          field_name: "max_range",
          label: "Max Duration Range",
          type: "number",
          props: { required: true, placeholder: "Enter Max Duration Range" },
        },
        {
          field_name: "duration",
          label: "Duration",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Duration",
            options: [
              { value: "MINUTES", label: "Minutes" },
              { value: "HOURS", label: "Hours" },
              { value: "DAYS", label: "Days" },
              { value: "WEEKS", label: "Weeks" },
              { value: "MONTHS", label: "Months" },
            ],
          },
        },
        {
          field_name: "rate_type",
          label: "Rate Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select Rate Type",
            options: [
              { value: "ONCE", label: "Once" },
              { value: "MINUTELY", label: "Minutely" },
              { value: "HOURLY", label: "Hourly" },
              { value: "DAILY", label: "Daily" },
              { value: "WEEKLY", label: "Weekly" },
              { value: "MONTHLY", label: "Monthly" },
            ],
          },
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          props: {
            required: true,
            placeholder: "Select City",
            options:
              districts?.map((district: any) => ({
                value: district.id,
                label: district.name,
              })) || [],
          },
        },
        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: { required: true, placeholder: "Enter amount" },
        },
      ],
    },

    {
      title: "Car Details",
      fields: [
        {
          field_name: "car_size",
          label: "Car Size",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Car Size",
            options:
              carSizes?.results?.map((item) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "car_type",
          label: "Car Type",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Car Type",
            options:
              carTypes?.results?.map((item) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "vehicle_type",
          label: "Vehicle Type",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Vehicle Type",
            options:
              (vehicleTypes &&
                vehicleTypes?.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))) ||
              [],
          },
        },
        {
          field_name: "car_size_dependant",
          label: "Car Size Dependant",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Car Size Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "car_type_dependant",
          label: "Car Type Dependant",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Car Type Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
        {
          field_name: "vehicle_type_dependant",
          label: "Vehicle Type Dependant",
          type: "select",
          props: {
            required: false,
            placeholder: "Select Vehicle Type Dependant",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        },
      ],
    },
  ];

  const title = "Service Rates";
  const redirect = getRegister(URL_SERVICE_RATES);

  return {
    sections,
    title,
    redirect,
    serviceTypes,
    loadingServiceTypes,
    districts,
    loadingDistricts,
    loadingServiceRates,
    serviceRates,
    vehicleTypes,
    loadingVehicleTypes,
    carTypes,
    loadingCarTypes,
    carSizes,
    loadingCarSizes,
  };
};
